/**
 * Utility functions for formatting data throughout the application
 */

// Date formatting utilities
export const formatDate = (date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  });
};

export const formatDateTime = (date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  });
};

export const formatRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  
  return formatDate(dateObj);
};

// Number formatting utilities
export const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
  return num.toLocaleString('en-US', options);
};

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

// Text formatting utilities
export const truncateText = (text: string, maxLength: number, suffix: string = '...'): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const capitalizeWords = (text: string): string => {
  if (!text) return '';
  return text
    .split(' ')
    .map(word => capitalizeFirst(word))
    .join(' ');
};

export const getInitials = (name: string, maxLength: number = 2): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .substring(0, maxLength)
    .toUpperCase();
};

export const formatEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned;
};

// File size formatting
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Progress formatting
export const formatProgress = (current: number, total: number): string => {
  if (total === 0) return '0%';
  const percentage = Math.round((current / total) * 100);
  return `${percentage}%`;
};

export const formatProgressBar = (progress: number): { width: string; percentage: string } => {
  const percentage = Math.min(Math.max(progress, 0), 100);
  return {
    width: `${percentage}%`,
    percentage: `${Math.round(percentage)}%`
  };
};

// Status formatting
export const formatStatus = (isActive: boolean, activeText: string = 'Active', inactiveText: string = 'Inactive'): string => {
  return isActive ? activeText : inactiveText;
};

export const getStatusColor = (isActive: boolean): string => {
  return isActive 
    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
};

// Search and filter utilities
export const normalizeSearchTerm = (term: string): string => {
  return term.toLowerCase().trim();
};

export const matchesSearch = (text: string, searchTerm: string): boolean => {
  return normalizeSearchTerm(text).includes(normalizeSearchTerm(searchTerm));
};

// Array formatting utilities
export const formatList = (items: string[], maxItems: number = 3, separator: string = ', '): string => {
  if (items.length <= maxItems) {
    return items.join(separator);
  }
  
  const visibleItems = items.slice(0, maxItems);
  const remainingCount = items.length - maxItems;
  
  return `${visibleItems.join(separator)} and ${remainingCount} more`;
};

// URL formatting
export const formatImageUrl = (filename: string, baseUrl?: string): string => {
  if (!filename) return '';
  
  // If it's already a full URL, return as is
  if (filename.startsWith('http')) {
    return filename;
  }
  
  const base = baseUrl || import.meta.env.VITE_BACKEND_ENDPOINT || 'https://api.production.gnzabe.com';
  return `${base}/images/${filename}`;
};

// Validation formatting
export const formatValidationError = (error: string): string => {
  // Remove common validation prefixes
  return error
    .replace(/^Error: /, '')
    .replace(/^Validation error: /, '')
    .trim();
};

// Time formatting
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

// Score formatting
export const formatScore = (score: number, total: number = 5): string => {
  return `${score}/${total}`;
};

export const formatAverageScore = (scores: number[]): string => {
  if (scores.length === 0) return '0/5';
  const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  return formatScore(Math.round(average));
};
